// C7 — Análisis de personal (coordinador).
//
// Rediseño 2026-06: ranking de bonos por persona vs capacidad estándar.
//   - Header: título + segmented período (Hoy/Esta semana/Este mes/Rango) — patrón C5
//   - Filtro Área (sin Planta: todo es Sopó, alineado con C5)
//   - 4 KPI cards con InfoHint (cumplimiento promedio vs capacidad)
//   - Tabla LÍDERES: solo activos (bloques>0), orden default cumplimiento DESC,
//     # de ranking, mini-barra de cumplimiento, inactivos en sección colapsable
//   - Tabla SUPERVISORES: producido/esperado/cumplimiento + gestión + oportunidad
//   - Mini-barra de cumplimiento (semáforo 90/70) + chevron afford sin navegación
//
// Contrato de query: {desde, hasta, area_id, linea_id, turno_id,
// supervisor_user_id}. El backend deriva
// tenant del JWT. PersonalLider trae area_nombre (un líder vive en UNA línea),
// que se muestra como subtexto bajo el nombre. Los supervisores NO llevan área
// (supervisan toda la planta).

import { useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  inicioSemanaOperativa,
  type AnalisisPersonalQuery,
  type CoordinadorAnalisisPersonal,
  type PersonalLider,
  type PersonalSupervisor,
} from 'schemas';
import { Card, CardContent } from '../../components/ui/card';
import { InfoHint } from '../../components/ui/info-hint';
import { Input } from '../../components/ui/input';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TFoot, TH, THead, TR, Table } from '../../components/ui/table';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { useCoordinadorAnalisisPersonal } from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { semaforoCumplimiento, toneTextClass, type Tone } from '../../lib/semaforo';

// =============================================================================
// Tipos locales
// =============================================================================

type Periodo = 'hoy' | 'semana' | 'mes' | 'rango';

type SortLider =
  | 'cumplimiento_meta_pct'
  | 'cantidad_real'
  | 'esperado_capacidad'
  | 'bloques_capturados'
  | 'dias_capturados';
type SortSupervisor =
  | 'producido'
  | 'esperado_capacidad'
  | 'cumplimiento_meta_pct'
  | 'turnos'
  | 'validaciones'
  | 'invalidaciones'
  | 'capturas_delegadas'
  | 'paros_cerrados'
  | 'oportunidad_min'
  | 'pendientes_validacion';
type SortDir = 'asc' | 'desc';

// =============================================================================
// Textos de ayuda contextual
// =============================================================================

const HINT = {
  producido:
    'Paquetes producidos y validados en el período. Solo cuenta lo validado por el supervisor.',
  esperado: 'Lo que la persona podía producir a capacidad estándar en las horas que cubrió.',
  cumplimiento: 'Producido ÷ Esperado (capacidad). Verde ≥90, ámbar 70-89, rojo <70.',
  oportunidad: 'Tiempo promedio entre que el líder captura y el supervisor valida.',
  pendientes:
    'Bloques capturados en los turnos que cubre el supervisor que aún no tienen su validación. Mientras existan, la OP de esos bloques queda retenida del corte de facturación (regla DN4). Se validan entrando a cada línea.',
} as const;

// =============================================================================
// Helpers
// =============================================================================

function fmtNumero(n: number): string {
  return Math.round(n).toLocaleString('es-CO');
}

/** Fecha local YYYY-MM-DD. */
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Período → {desde, hasta}. La semana es la operativa del tenant (default lunes). */
function periodoAFiltros(
  periodo: Exclude<Periodo, 'rango'>,
  diaInicioSemana = 1,
): { desde: string; hasta: string } {
  const hoy = new Date();
  const hastaStr = ymd(hoy);
  if (periodo === 'hoy') {
    return { desde: hastaStr, hasta: hastaStr };
  }
  if (periodo === 'semana') {
    return { desde: inicioSemanaOperativa(hastaStr, diaInicioSemana), hasta: hastaStr };
  }
  // mes
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  return { desde: ymd(primerDia), hasta: hastaStr };
}

/** Tono de barra → clase de fondo (relleno). */
function toneBarClass(tone: Tone): string {
  return tone === 'ok'
    ? 'bg-ok'
    : tone === 'warn'
      ? 'bg-warn'
      : tone === 'crit'
        ? 'bg-crit'
        : 'bg-fg-tertiary';
}

// =============================================================================
// Componente principal
// =============================================================================

export function C7AnalisisPersonalPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const { diaInicioSemana } = useTenantConfig();

  // Filtros persistidos en la URL: un refresh los conserva y "atrás" desde el
  // perfil de un líder los restaura (antes vivían en useState y se perdían).
  // replace:true → cambiar un filtro no apila historial; el "atrás" sale de C7.
  const [searchParams, setSearchParams] = useSearchParams();
  const setParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (!v) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  };

  const periodo = (searchParams.get('periodo') as Periodo | null) ?? 'semana';
  const areaId = searchParams.get('area') ?? undefined;
  const lineaId = searchParams.get('linea') ?? undefined;
  const turnoId = searchParams.get('turno') ?? undefined;
  const supervisorId = searchParams.get('supervisor') ?? undefined;

  const setPeriodo = (p: Periodo) => setParams({ periodo: p === 'semana' ? undefined : p });
  // Cambiar de área invalida la línea elegida (las líneas cuelgan del área):
  // se limpia para no dejar un filtro cruzado que devuelva vacío.
  const setAreaId = (id: string | undefined) => setParams({ area: id, linea: undefined });
  const setLineaId = (id: string | undefined) => setParams({ linea: id });
  const setTurnoId = (id: string | undefined) => setParams({ turno: id });
  const setSupervisorId = (id: string | undefined) => setParams({ supervisor: id });

  // Rango personalizado (solo activo cuando periodo === 'rango'). Default:
  // últimos 7 días (hoy-6 → hoy). También vive en la URL (desde/hasta).
  const defaultRango = useMemo(() => {
    const hoy = new Date();
    const inicio = new Date(hoy);
    inicio.setDate(hoy.getDate() - 6);
    return { desde: ymd(inicio), hasta: ymd(hoy) };
  }, []);
  const rango = {
    desde: searchParams.get('desde') ?? defaultRango.desde,
    hasta: searchParams.get('hasta') ?? defaultRango.hasta,
  };
  const setRango = (r: { desde: string; hasta: string }) =>
    setParams({ desde: r.desde, hasta: r.hasta });

  // Rango válido solo si ambas fechas presentes y desde ≤ hasta.
  const rangoValido = Boolean(rango.desde && rango.hasta && rango.desde <= rango.hasta);

  const [sortLider, setSortLider] = useState<SortLider>('cumplimiento_meta_pct');
  const [sortLiderDir, setSortLiderDir] = useState<SortDir>('desc');
  const [sortSup, setSortSup] = useState<SortSupervisor>('cumplimiento_meta_pct');
  const [sortSupDir, setSortSupDir] = useState<SortDir>('desc');
  const [verInactivos, setVerInactivos] = useState(false);

  // Período → filtros enviados al endpoint.
  // Contrato: {desde, hasta, area_id, linea_id, turno_id, supervisor_user_id}.
  const filtros: AnalisisPersonalQuery = useMemo(() => {
    const base = {
      area_id: areaId,
      linea_id: lineaId,
      turno_id: turnoId,
      supervisor_user_id: supervisorId,
    };
    if (periodo === 'rango') {
      return { desde: rango.desde, hasta: rango.hasta, ...base };
    }
    const { desde, hasta } = periodoAFiltros(periodo, diaInicioSemana);
    return { desde, hasta, ...base };
  }, [periodo, rango.desde, rango.hasta, areaId, lineaId, turnoId, supervisorId, diaInicioSemana]);

  // Con un rango inválido NO disparamos la query (mantiene su key anterior).
  const filtrosCommitRef = useRef<AnalisisPersonalQuery>(filtros);
  if (periodo !== 'rango' || rangoValido) {
    filtrosCommitRef.current = filtros;
  }
  const filtrosEfectivos = filtrosCommitRef.current;

  const { data, isLoading, isFetching, isError, error } =
    useCoordinadorAnalisisPersonal(filtrosEfectivos);

  // Hooks ANTES de early returns (regla React).
  // Líderes: separar activos (bloques>0) de inactivos; ordenar cada uno.
  const { lideresActivos, lideresInactivos } = useMemo(() => {
    const todos = data?.lideres ?? [];
    const cmp = (a: PersonalLider, b: PersonalLider) => {
      const va = a[sortLider];
      const vb = b[sortLider];
      const aNull = va === null;
      const bNull = vb === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1; // nulls al final, independiente de la dirección
      if (bNull) return -1;
      const diff = (va as number) - (vb as number);
      return sortLiderDir === 'asc' ? diff : -diff;
    };
    const activos = todos.filter((l) => l.bloques_capturados > 0).sort(cmp);
    const inactivos = todos.filter((l) => l.bloques_capturados === 0).sort(cmp);
    return { lideresActivos: activos, lideresInactivos: inactivos };
  }, [data?.lideres, sortLider, sortLiderDir]);

  const supervisoresSorted = useMemo(() => {
    if (!data?.supervisores) return [];
    const arr = [...data.supervisores];
    arr.sort((a, b) => {
      const va = a[sortSup];
      const vb = b[sortSup];
      const aNull = va === null;
      const bNull = vb === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      const diff = (va as number) - (vb as number);
      return sortSupDir === 'asc' ? diff : -diff;
    });
    return arr;
  }, [data?.supervisores, sortSup, sortSupDir]);

  // Totales al pie de cada tabla (pedido Robotpack 2026-08). El cumplimiento
  // total es PONDERADO (Σproducido / Σesperado), no promedio de porcentajes.
  const totalLideres = useMemo(() => {
    const producido = lideresActivos.reduce((acc, l) => acc + l.cantidad_real, 0);
    const esperado = lideresActivos.reduce((acc, l) => acc + l.esperado_capacidad, 0);
    const horas = lideresActivos.reduce((acc, l) => acc + l.bloques_capturados, 0);
    return {
      producido,
      esperado,
      cumplimiento: esperado > 0 ? (producido / esperado) * 100 : null,
      horas,
    };
  }, [lideresActivos]);

  const totalSupervisores = useMemo(() => {
    const acc = {
      producido: 0,
      esperado: 0,
      turnos: 0,
      validaciones: 0,
      invalidaciones: 0,
      delegadas: 0,
      paros: 0,
      pendientes: 0,
      pendientesPaq: 0,
    };
    for (const s of supervisoresSorted) {
      acc.producido += s.producido;
      acc.esperado += s.esperado_capacidad;
      acc.turnos += s.turnos;
      acc.validaciones += s.validaciones;
      acc.invalidaciones += s.invalidaciones;
      acc.delegadas += s.capturas_delegadas;
      acc.paros += s.paros_cerrados;
      acc.pendientes += s.pendientes_validacion ?? 0;
      acc.pendientesPaq += s.pendientes_paq ?? 0;
    }
    return {
      ...acc,
      cumplimiento: acc.esperado > 0 ? (acc.producido / acc.esperado) * 100 : null,
    };
  }, [supervisoresSorted]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando análisis…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  const toggleLider = (k: SortLider) => {
    if (sortLider === k) setSortLiderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortLider(k);
      setSortLiderDir('desc');
    }
  };
  const toggleSup = (k: SortSupervisor) => {
    if (sortSup === k) setSortSupDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortSup(k);
      setSortSupDir('desc');
    }
  };

  // Drill al perfil individual de la persona, propagando el período actual.
  const irAPerfil = (userId: string) => {
    const params = new URLSearchParams();
    if (filtrosEfectivos.desde) params.set('desde', filtrosEfectivos.desde);
    if (filtrosEfectivos.hasta) params.set('hasta', filtrosEfectivos.hasta);
    const qs = params.toString();
    navigate(`/coordinador/personal/${userId}${qs ? `?${qs}` : ''}`);
  };

  // Filtros aplicados que no arrojan capturas (ej. un turno que aún no corrió):
  // mostramos un vacío explícito en vez de tarjetas en cero + tablas vacías.
  const sinDatos =
    data.totales.cantidad_real_total === 0 &&
    data.totales.validaciones_total === 0 &&
    data.totales.invalidaciones_total === 0 &&
    data.totales.capturas_delegadas_total === 0;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <HeaderRibbon
          data={data}
          periodo={periodo}
          onPeriodo={setPeriodo}
          rango={rango}
          onRango={setRango}
          rangoValido={rangoValido}
        />

        <FiltrosBar
          data={data}
          areaId={areaId}
          onArea={setAreaId}
          lineaId={lineaId}
          onLinea={setLineaId}
          turnoId={turnoId}
          onTurno={setTurnoId}
          supervisorId={supervisorId}
          onSupervisor={setSupervisorId}
        />

        {isFetching && !isLoading ? (
          <p className="-mt-1 text-[11px] text-fg-tertiary">Actualizando…</p>
        ) : null}

        <section>
          <Card>
            <CardContent className="grid grid-cols-2 gap-4 p-5 pt-5 md:grid-cols-4">
              <KpiCard
                label="Líderes evaluados"
                value={String(data.totales.lideres_evaluados)}
                sub={`${data.totales.supervisores_evaluados} supervisores`}
              />
              <KpiCard
                label="Producido"
                hint={HINT.producido}
                value={fmtNumero(data.totales.cantidad_real_total)}
                sub={`vs ${fmtNumero(data.totales.esperado_capacidad_total)} esperado`}
                divider
              />
              <KpiCard
                label="Cumplimiento prom."
                hint={HINT.cumplimiento}
                value={
                  data.totales.cumplimiento_meta_promedio_pct !== null
                    ? data.totales.cumplimiento_meta_promedio_pct.toFixed(0)
                    : '—'
                }
                valueSuffix={data.totales.cumplimiento_meta_promedio_pct !== null ? '%' : ''}
                sub={`${data.filtros.desde} a ${data.filtros.hasta}`}
                subTone={semaforoCumplimiento(data.totales.cumplimiento_meta_promedio_pct)}
                divider
              />
              <KpiCard
                label="Acciones supervisión"
                value={String(
                  data.totales.validaciones_total +
                    data.totales.invalidaciones_total +
                    data.totales.capturas_delegadas_total,
                )}
                sub={`${data.totales.validaciones_total} val · ${data.totales.invalidaciones_total} inval · ${data.totales.capturas_delegadas_total} deleg`}
                divider
              />
            </CardContent>
          </Card>
        </section>

        {sinDatos ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-fg-secondary">
              Sin capturas para los filtros seleccionados en este período.
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Tabla líderes (ranking de bonos) */}
            <section>
              <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Líderes ({lideresActivos.length})
              </h2>
              {lideresActivos.length === 0 && lideresInactivos.length === 0 ? (
                <Card>
                  <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                    No hay líderes en este tenant.
                  </CardContent>
                </Card>
              ) : (
                <Table maxHeight="70vh">
                  <THead sticky>
                    <TR>
                      <TH align="right">#</TH>
                      <TH>Líder</TH>
                      <TH align="right">
                        <SortHeader
                          label="Producido"
                          hint={HINT.producido}
                          active={sortLider === 'cantidad_real'}
                          dir={sortLiderDir}
                          onClick={() => toggleLider('cantidad_real')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Esperado"
                          hint={HINT.esperado}
                          active={sortLider === 'esperado_capacidad'}
                          dir={sortLiderDir}
                          onClick={() => toggleLider('esperado_capacidad')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Cumplimiento"
                          hint={HINT.cumplimiento}
                          active={sortLider === 'cumplimiento_meta_pct'}
                          dir={sortLiderDir}
                          onClick={() => toggleLider('cumplimiento_meta_pct')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Horas"
                          active={sortLider === 'bloques_capturados'}
                          dir={sortLiderDir}
                          onClick={() => toggleLider('bloques_capturados')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Días"
                          active={sortLider === 'dias_capturados'}
                          dir={sortLiderDir}
                          onClick={() => toggleLider('dias_capturados')}
                        />
                      </TH>
                      <TH align="right" />
                    </TR>
                  </THead>
                  <TBody>
                    {lideresActivos.map((l, i) => (
                      <FilaLider key={l.user_id} l={l} rank={i + 1} onAbrir={irAPerfil} />
                    ))}
                    {verInactivos &&
                      lideresInactivos.map((l) => (
                        <FilaLider key={l.user_id} l={l} rank={null} atenuada onAbrir={irAPerfil} />
                      ))}
                  </TBody>
                  {/* Total al pie (pedido Robotpack 2026-08). Sticky para que
                      quede visible mientras se scrollean las filas. */}
                  <TFoot className="sticky bottom-0 z-10">
                    <TR className="hover:bg-surface-overlay">
                      <TD align="right" />
                      <TD className="font-medium text-fg">
                        Total ({lideresActivos.length} líder
                        {lideresActivos.length === 1 ? '' : 'es'})
                      </TD>
                      <TD align="right" numeric className="font-medium text-fg">
                        {fmtNumero(totalLideres.producido)}
                      </TD>
                      <TD align="right" numeric className="font-medium text-fg">
                        {fmtNumero(totalLideres.esperado)}
                      </TD>
                      <CumplimientoCell pct={totalLideres.cumplimiento} />
                      <TD align="right" numeric className="font-medium text-fg">
                        {totalLideres.horas}
                      </TD>
                      <TD align="right" numeric className="text-fg-tertiary">
                        —
                      </TD>
                      <TD align="right" />
                    </TR>
                  </TFoot>
                </Table>
              )}
              {lideresInactivos.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setVerInactivos((v) => !v)}
                  className="mt-2 text-[11px] text-fg-tertiary underline hover:text-fg-secondary"
                >
                  {verInactivos
                    ? 'Ocultar inactivos'
                    : `Ver ${lideresInactivos.length} inactivo${lideresInactivos.length === 1 ? '' : 's'}`}
                </button>
              ) : null}
            </section>

            {/* Tabla supervisores */}
            <section>
              <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Supervisores ({supervisoresSorted.length})
              </h2>
              {supervisoresSorted.length === 0 ? (
                <Card>
                  <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                    No hay supervisores activos en este tenant.
                  </CardContent>
                </Card>
              ) : (
                <Table maxHeight="70vh">
                  <THead sticky>
                    <TR>
                      <TH>Supervisor</TH>
                      <TH align="right">
                        <SortHeader
                          label="Producido"
                          hint={HINT.producido}
                          active={sortSup === 'producido'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('producido')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Esperado"
                          hint={HINT.esperado}
                          active={sortSup === 'esperado_capacidad'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('esperado_capacidad')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Cumplimiento"
                          hint={HINT.cumplimiento}
                          active={sortSup === 'cumplimiento_meta_pct'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('cumplimiento_meta_pct')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Turnos"
                          active={sortSup === 'turnos'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('turnos')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Val"
                          active={sortSup === 'validaciones'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('validaciones')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Pend."
                          hint={HINT.pendientes}
                          active={sortSup === 'pendientes_validacion'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('pendientes_validacion')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Inval"
                          active={sortSup === 'invalidaciones'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('invalidaciones')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Deleg"
                          active={sortSup === 'capturas_delegadas'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('capturas_delegadas')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Paros confirm."
                          active={sortSup === 'paros_cerrados'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('paros_cerrados')}
                        />
                      </TH>
                      <TH align="right">
                        <SortHeader
                          label="Oportunidad"
                          hint={HINT.oportunidad}
                          active={sortSup === 'oportunidad_min'}
                          dir={sortSupDir}
                          onClick={() => toggleSup('oportunidad_min')}
                        />
                      </TH>
                      <TH align="right" />
                    </TR>
                  </THead>
                  <TBody>
                    {supervisoresSorted.map((s) => (
                      <FilaSupervisor key={s.user_id} s={s} onAbrir={irAPerfil} />
                    ))}
                  </TBody>
                  <TFoot className="sticky bottom-0 z-10">
                    <TR className="hover:bg-surface-overlay">
                      <TD className="font-medium text-fg">Total ({supervisoresSorted.length})</TD>
                      <TD align="right" numeric className="font-medium text-fg">
                        {fmtNumero(totalSupervisores.producido)}
                      </TD>
                      <TD align="right" numeric className="font-medium text-fg">
                        {fmtNumero(totalSupervisores.esperado)}
                      </TD>
                      <CumplimientoCell pct={totalSupervisores.cumplimiento} />
                      <TD align="right" numeric className="font-medium text-fg">
                        {totalSupervisores.turnos}
                      </TD>
                      <TD align="right" numeric className="font-medium text-ok">
                        {totalSupervisores.validaciones}
                      </TD>
                      <TD
                        align="right"
                        numeric
                        className={
                          totalSupervisores.pendientes > 0
                            ? 'font-medium text-warn'
                            : 'font-medium text-fg-tertiary'
                        }
                        title={`${fmtNumero(totalSupervisores.pendientesPaq)} paq sin validar`}
                      >
                        {totalSupervisores.pendientes}
                      </TD>
                      <TD
                        align="right"
                        numeric
                        className={
                          totalSupervisores.invalidaciones > 0
                            ? 'font-medium text-warn'
                            : 'font-medium text-fg-tertiary'
                        }
                      >
                        {totalSupervisores.invalidaciones}
                      </TD>
                      <TD align="right" numeric className="font-medium text-info">
                        {totalSupervisores.delegadas}
                      </TD>
                      <TD align="right" numeric className="font-medium text-fg">
                        {totalSupervisores.paros}
                      </TD>
                      <TD align="right" numeric className="text-fg-tertiary">
                        —
                      </TD>
                      <TD align="right" />
                    </TR>
                  </TFoot>
                </Table>
              )}
            </section>
          </>
        )}
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// Mini-barra de cumplimiento (semáforo) + %
// =============================================================================

function CumplimientoCell({ pct }: { pct: number | null }) {
  if (pct === null) {
    return (
      <TD align="right" numeric>
        <span className="text-fg-tertiary">—</span>
      </TD>
    );
  }
  const tone = semaforoCumplimiento(pct);
  const ancho = Math.min(100, Math.max(0, pct)); // cap visual a 100%
  return (
    <TD align="right" numeric>
      <div className="flex items-center justify-end gap-2">
        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-overlay">
          <div
            className={cn('h-full rounded-full', toneBarClass(tone))}
            style={{ width: `${ancho}%` }}
          />
        </div>
        <span className={cn('font-medium', toneTextClass(tone))}>{pct.toFixed(0)}%</span>
      </div>
    </TD>
  );
}

// =============================================================================
// Filas
// =============================================================================

function FilaLider({
  l,
  rank,
  atenuada = false,
  onAbrir,
}: {
  l: PersonalLider;
  rank: number | null;
  atenuada?: boolean;
  onAbrir: (userId: string) => void;
}) {
  return (
    <TR
      className={cn('cursor-pointer', atenuada && 'opacity-60')}
      onClick={() => onAbrir(l.user_id)}
      title="Ver perfil individual"
    >
      <TD align="right" numeric className="text-fg-tertiary">
        {rank ?? '—'}
      </TD>
      <TD>
        <span className={cn('font-medium', atenuada ? 'text-fg-tertiary' : 'text-fg')}>
          {l.nombre} {l.apellido}
        </span>
        {(l.area_nombre || l.linea_nombre) && (
          <div className="text-xs text-fg-tertiary">
            {[l.area_nombre, l.linea_nombre].filter(Boolean).join(' · ')}
          </div>
        )}
      </TD>
      <TD align="right" numeric className={atenuada ? 'text-fg-tertiary' : 'text-fg'}>
        {fmtNumero(l.cantidad_real)}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {fmtNumero(l.esperado_capacidad)}
      </TD>
      <CumplimientoCell pct={l.cumplimiento_meta_pct} />
      <TD align="right" numeric className="text-fg-secondary">
        {l.bloques_capturados}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {l.dias_capturados}
      </TD>
      <TD align="right">
        <span className="text-fg-tertiary" aria-hidden>
          ›
        </span>
      </TD>
    </TR>
  );
}

function FilaSupervisor({
  s,
  onAbrir,
}: {
  s: PersonalSupervisor;
  onAbrir: (userId: string) => void;
}) {
  return (
    <TR className="cursor-pointer" onClick={() => onAbrir(s.user_id)} title="Ver perfil individual">
      <TD>
        <span className="font-medium text-fg">
          {s.nombre} {s.apellido}
        </span>
      </TD>
      <TD align="right" numeric className="text-fg">
        {fmtNumero(s.producido)}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {fmtNumero(s.esperado_capacidad)}
      </TD>
      <CumplimientoCell pct={s.cumplimiento_meta_pct} />
      <TD align="right" numeric className="text-fg-secondary">
        {s.turnos}
      </TD>
      <TD align="right" numeric className="text-ok">
        {s.validaciones}
      </TD>
      <TD
        align="right"
        numeric
        className={(s.pendientes_validacion ?? 0) > 0 ? 'text-warn' : 'text-fg-tertiary'}
        title={
          (s.pendientes_validacion ?? 0) > 0
            ? `${fmtNumero(s.pendientes_paq ?? 0)} paq sin validar — retienen la facturación de su OP`
            : 'Sin bloques pendientes de validar'
        }
      >
        {s.pendientes_validacion ?? 0}
      </TD>
      <TD align="right" numeric className={s.invalidaciones > 0 ? 'text-warn' : 'text-fg-tertiary'}>
        {s.invalidaciones}
      </TD>
      <TD align="right" numeric className="text-info">
        {s.capturas_delegadas}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {s.paros_cerrados}
      </TD>
      <TD align="right" numeric className="text-fg-secondary">
        {s.oportunidad_min !== null ? `+${Math.round(s.oportunidad_min)}m` : '—'}
      </TD>
      <TD align="right">
        <span className="text-fg-tertiary" aria-hidden>
          ›
        </span>
      </TD>
    </TR>
  );
}

// =============================================================================
// SortHeader — botón de sort + ⓘ hermano (el ⓘ no dispara el sort)
// =============================================================================

function SortHeader({
  label,
  hint,
  active,
  dir,
  onClick,
}: {
  label: string;
  hint?: React.ReactNode;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <span className="inline-flex flex-row-reverse items-center gap-1">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1',
          active ? 'text-fg font-medium' : 'text-fg-tertiary hover:text-fg-secondary',
        )}
      >
        {label}
        {active && <span aria-hidden>{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
      {hint ? <InfoHint>{hint}</InfoHint> : null}
    </span>
  );
}

// =============================================================================
// HeaderRibbon — título + segmented período + inputs de rango
// =============================================================================

function HeaderRibbon({
  data,
  periodo,
  onPeriodo,
  rango,
  onRango,
  rangoValido,
}: {
  data: CoordinadorAnalisisPersonal;
  periodo: Periodo;
  onPeriodo: (p: Periodo) => void;
  rango: { desde: string; hasta: string };
  onRango: (r: { desde: string; hasta: string }) => void;
  rangoValido: boolean;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
      <div>
        <h1 className="text-xl font-medium text-fg">Análisis de personal</h1>
        <p className="mt-1 text-[11px] text-fg-tertiary">
          {data.tenant.nombre} · desempeño de líderes y supervisores
        </p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <SegmentedControl<Periodo>
          size="sm"
          value={periodo}
          onChange={onPeriodo}
          options={[
            { value: 'hoy', label: 'Hoy' },
            { value: 'semana', label: 'Esta semana' },
            { value: 'mes', label: 'Este mes' },
            { value: 'rango', label: 'Rango' },
          ]}
        />
        {periodo === 'rango' ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Desde
              </span>
              <Input
                type="date"
                value={rango.desde}
                max={rango.hasta || undefined}
                onChange={(e) => onRango({ ...rango, desde: e.target.value })}
                className="h-8 w-[150px]"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Hasta
              </span>
              <Input
                type="date"
                value={rango.hasta}
                min={rango.desde || undefined}
                onChange={(e) => onRango({ ...rango, hasta: e.target.value })}
                className="h-8 w-[150px]"
              />
            </div>
            {!rangoValido ? (
              <span className="text-[11px] text-warn">La fecha "Desde" debe ser ≤ "Hasta".</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}

// =============================================================================
// FiltrosBar — Área + Turno + Supervisor (sin Planta: todo es Sopó)
// =============================================================================

function FiltrosBar({
  data,
  areaId,
  onArea,
  lineaId,
  onLinea,
  turnoId,
  onTurno,
  supervisorId,
  onSupervisor,
}: {
  data: CoordinadorAnalisisPersonal;
  areaId: string | undefined;
  onArea: (id: string | undefined) => void;
  lineaId: string | undefined;
  onLinea: (id: string | undefined) => void;
  turnoId: string | undefined;
  onTurno: (id: string | undefined) => void;
  supervisorId: string | undefined;
  onSupervisor: (id: string | undefined) => void;
}) {
  // Con un área elegida solo se ofrecen SUS líneas (encadenado área → línea).
  const lineasVisibles = areaId
    ? data.recursos.lineas.filter((l) => l.area_id === areaId)
    : data.recursos.lineas;
  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-overlay/40 p-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="flex flex-col gap-1">
        <Label htmlFor="c7-area">Área</Label>
        <Select
          id="c7-area"
          value={areaId ?? ''}
          onChange={(e) => onArea(e.target.value || undefined)}
          disabled={data.recursos.areas.length === 0}
          className="max-w-xs"
        >
          <option value="">Todas</option>
          {data.recursos.areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombre}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="c7-linea">Línea</Label>
        <Select
          id="c7-linea"
          value={lineaId ?? ''}
          onChange={(e) => onLinea(e.target.value || undefined)}
          disabled={lineasVisibles.length === 0}
          className="max-w-xs"
        >
          <option value="">Todas</option>
          {lineasVisibles.map((l) => (
            <option key={l.id} value={l.id}>
              {l.nombre}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="c7-turno">Turno</Label>
        <Select
          id="c7-turno"
          value={turnoId ?? ''}
          onChange={(e) => onTurno(e.target.value || undefined)}
          disabled={data.recursos.turnos.length === 0}
          className="max-w-xs"
        >
          <option value="">Todos</option>
          {data.recursos.turnos.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="c7-supervisor">Supervisor</Label>
        <Select
          id="c7-supervisor"
          value={supervisorId ?? ''}
          onChange={(e) => onSupervisor(e.target.value || undefined)}
          disabled={data.recursos.supervisores.length === 0}
          className="max-w-xs"
        >
          <option value="">Todos</option>
          {data.recursos.supervisores.map((s) => (
            <option key={s.user_id} value={s.user_id}>
              {s.nombre} {s.apellido}
            </option>
          ))}
        </Select>
      </div>
    </section>
  );
}
